import { Table } from 'components';
import { type VFC, useCallback } from 'react';
import type { ColumnsType, SorterResult } from 'antd/lib/table/interface';
import type { SerializedPerson, SerializedPersonWithPhotoUrl } from '@enmon/commons/lib/tools';
import { useDeletePerson } from 'api/hooks/person/useDelete';
import { Image } from 'antd';
import { FALLBACK_IMAGE_URL } from 'routes/portfolio/locations/routes/list/routes/detail/const';
import { usePersonsFilterWithPagination } from '../hooks/usePersonsFilterWithPagination';
import { PersonsActionButton } from './ActionButton';

interface PersonsTableProps {
  data: SerializedPersonWithPhotoUrl[];
  isLoading: boolean;
  onSuccess: () => void;
  onEdit: (person: SerializedPersonWithPhotoUrl) => void;
}

export const PersonsTable: VFC<PersonsTableProps> = ({ data, isLoading, onSuccess, onEdit }) => {
  const { setFilterState, pagination } = usePersonsFilterWithPagination();
  const { mutate: deletePerson, isLoading: isDeleting } = useDeletePerson({ onSuccess });

  const handleTableChange = (sorter: SorterResult<SerializedPersonWithPhotoUrl>) => {
    const sorterField = Array.isArray(sorter.field) ? sorter.field.slice(-1) : sorter.field;

    setFilterState({
      ...(sorter.order &&
        sorterField && {
          sort: `${sorterField as string}:${sorter.order.slice(0, -3)}`,
        }),
    });
  };

  const handleDelete = useCallback((id: SerializedPersonWithPhotoUrl['id']) => deletePerson(id), [deletePerson]);

  const columns: ColumnsType<SerializedPersonWithPhotoUrl> = [
    {
      title: 'Photo',
      key: 'photoUrl',
      sorter: false,
      render: (_, { photoUrl }) => (
        <Image
          fallback={FALLBACK_IMAGE_URL}
          height={128}
          preview={false}
          src={photoUrl ?? FALLBACK_IMAGE_URL}
          style={{ borderRadius: '32px', padding: '2px' }}
          width={128}
        />
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name' satisfies keyof SerializedPerson,
      key: 'name',
      sorter: true,
      width: 250,
    },
    {
      title: 'Contact',
      dataIndex: 'contact' satisfies keyof SerializedPerson,
      key: 'contact',
      sorter: true,
    },
    {
      title: 'Email',
      dataIndex: 'email' satisfies keyof SerializedPerson,
      key: 'email',
      sorter: true,
      width: 250,
    },
    {
      title: 'Position',
      dataIndex: 'position' satisfies keyof SerializedPerson,
      key: 'position',
      sorter: true,
    },
    {
      width: 150,
      title: 'Actions',
      key: 'action',
      fixed: 'right',
      render: (_, person) => (
        <PersonsActionButton
          isLoading={isLoading || isDeleting}
          onClick={() => onEdit(person)}
          onDelete={() => handleDelete(person.id)}
        />
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={data}
      loading={isLoading || isDeleting}
      pagination={{
        ...pagination,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} Persons`,
      }}
      rowKey={item => item.id}
      scroll={{ x: 'fit-content' }}
      onChange={(_, __, sorter) => handleTableChange(Array.isArray(sorter) ? sorter[0] : sorter)}
    />
  );
};
